import React, { memo, useEffect, useRef } from 'react';
import { numericFormatter } from 'react-number-format';
import { Checkbox, Popup } from 'semantic-ui-react';
import { useCellBoundingBox, useRowBoundingBox } from '../../../../../../stores/boundingBoxesStore';
import {
    BoundingBoxType,
    ImportDescriptorDataTypeType,
    ImportDescriptorIdType,
    ImportDescriptorSourceType,
    ReportIdType,
    FieldValueType,
} from '../../../../../../types';
import { ImageHighlighterWrapper } from '../../../../../imageHighlighter/imageHighlighter';
import ImagePopup from '../../../../../imagePopup';
import {
    useImportTableStoreReportsEditMode,
    useImportTableStoreDescriptor,
    useImportTableStoreFieldError,
    useImportTableStoreIsCellActive,
    useImportTableStoreCoordinatesActions,
    useImportTableStoreReportActions,
    useImportTableStoreReportFieldValue,
    useImportTableStoreInitialDescriptorType,
} from '../../../../context/importTableContext';
import FieldErrorPopup from '../../../fieldErrorPopup';
import InfoPopup from '../../../infoPopup';
import NumberInput from '../../../numberInput';
import { DECIMAL_SCALE, DECIMAL_SEPARATOR, THOUSAND_SEPARATOR } from '../../../numberInput/constants';
import { getReportCellStyling, getIsDescriptorNumericType } from './utils';
import {
    IMPORT_DESCRIPTOR_SOURCE,
    IMPORT_DESCRIPTOR_TYPE,
    FIELD_VALUE_SOURCE,
    HIGHLIGHTER_VARIANT,
} from '../../../../../../constants/generalConstants';
import { IMAGE_POPUP_MODE } from '../../../../../imagePopup/constants';
import { useImageStoreActions } from '../../../../../../stores/imageStore';
import { NO_OP_CALC } from '../../../../../../../../../constnats/schemaConstants';

type ReportCellPropsType = {
    descriptorId: ImportDescriptorIdType;
    reportId: ReportIdType;
    descriptorIndex: number;
    reportIndex: number;
    imagePopupOpen: boolean;
    onChange?: (data: any) => void;
};
const ReportCell = memo(function ReportCell({
    descriptorId,
    reportId,
    descriptorIndex,
    reportIndex,
    imagePopupOpen,
    onChange = () => {},
}: ReportCellPropsType) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const descriptor = useImportTableStoreDescriptor(descriptorId);
    const initialDescriptorDataType = useImportTableStoreInitialDescriptorType(descriptorId);
    const editing = useImportTableStoreReportsEditMode();
    const fieldValue = useImportTableStoreReportFieldValue(descriptorId, reportId, true);
    const { enableReportsEditing, updateReportField, updateReportFieldCalculationDisabled } =
        useImportTableStoreReportActions();
    const rowBoundingBox = useRowBoundingBox(descriptorId);
    const boundingBox = useCellBoundingBox(fieldValue.sourceCellId);
    const fieldErrors = useImportTableStoreFieldError(descriptorId, reportId);
    const isCellActive = useImportTableStoreIsCellActive(descriptorIndex, reportIndex);
    const { setCoordinates: setCellCoordinates } = useImportTableStoreCoordinatesActions();
    const { setCoordinates } = useImageStoreActions();

    const { styles, classes } = getReportCellStyling(fieldValue, editing, isCellActive);

    useEffect(() => {
        if (isCellActive && inputRef.current) {
            inputRef.current.focus();
            setCoordinates([
                { ...boundingBox, variant: HIGHLIGHTER_VARIANT.SECONDARY },
                { ...rowBoundingBox, variant: HIGHLIGHTER_VARIANT.PRIMARY },
            ]);
        } else if (!isCellActive && inputRef.current) {
            inputRef.current.blur();
        }
    }, [isCellActive, boundingBox, setCoordinates]);

    const handleCellFocus = () => {
        setCellCoordinates({ activeRow: descriptorIndex, activeCol: reportIndex });
    };

    return (
        <td style={styles} className={classes} onDoubleClick={enableReportsEditing}>
            <ImageHighlighterWrapper
                coordinates={[
                    { ...boundingBox, variant: HIGHLIGHTER_VARIANT.SECONDARY },
                    { ...rowBoundingBox, variant: HIGHLIGHTER_VARIANT.PRIMARY },
                ]}
            >
                {editing ? (
                    <EditReportCell
                        calculable={
                            (fieldValue.calculatedBy3D || fieldValue.manuallyDisabledVerticalCalc) && descriptor.numeric
                        }
                        boundingBox={boundingBox}
                        isActive={isCellActive}
                        onFocus={handleCellFocus}
                        inputRef={inputRef}
                        fieldError={fieldErrors}
                        descriptorSource={descriptor.source}
                        descriptorType={initialDescriptorDataType}
                        handleChange={(data) => {
                            if (data != fieldValue.normalizedValue) {
                                updateReportField(descriptorId, reportId, data, true);
                                onChange(data);
                            }
                        }}
                        handleUpdateReportFieldCalculationDisabled={(data) => {
                            if (data != fieldValue.manuallyDisabledVerticalCalc) {
                                updateReportFieldCalculationDisabled(descriptorId, reportId, data);
                                onChange(data);
                            }
                        }}
                        fieldValue={fieldValue}
                        imagePopupOpen={imagePopupOpen}
                    />
                ) : (
                    <ReadReportCell
                        boundingBox={boundingBox}
                        descriptorType={initialDescriptorDataType}
                        fieldValue={fieldValue}
                    />
                )}
            </ImageHighlighterWrapper>
        </td>
    );
});

export default ReportCell;

type EditReportCellPropsType = {
    calculable: boolean;
    fieldError: { error: boolean; message: string };
    fieldValue: FieldValueType;
    handleUpdateReportFieldCalculationDisabled: (data: boolean) => void;
    descriptorType: ImportDescriptorDataTypeType;
    descriptorSource: ImportDescriptorSourceType;
    handleChange: (data: string | number | null) => void;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    isActive?: boolean;
    imagePopupOpen: boolean;
    boundingBox: BoundingBoxType;
};
function EditReportCell({
    calculable,
    fieldError,
    fieldValue,
    handleUpdateReportFieldCalculationDisabled,
    descriptorType,
    descriptorSource,
    handleChange,
    inputRef,
    onFocus,
    isActive,
    imagePopupOpen,
    boundingBox,
}: EditReportCellPropsType) {
    const isCalculatedValue = fieldValue.calculatedBy3D;
    let inputClasses = 'cell-input report-cell-input outline--none border-w--1 ml-auto';

    if (fieldValue.normalizedValueMatchesMaster) {
        inputClasses += ' value--master-table';
    }

    return (
        <div className="flex full-height flex-align-center">
            <InfoPopup
                className={!!fieldValue.warnings.length || !!fieldValue.confirmations.length ? '' : 'invisible'}
                correction={fieldValue.correction}
                confirmations={fieldValue.confirmations}
                warnings={fieldValue.warnings}
            />
            {fieldError.error && <FieldErrorPopup message={fieldError.message} />}
            {calculable && (
                <Popup
                    trigger={
                        <Checkbox
                            checked={!fieldValue.manuallyDisabledVerticalCalc}
                            onChange={() =>
                                handleUpdateReportFieldCalculationDisabled(!fieldValue.manuallyDisabledVerticalCalc)
                            }
                        />
                    }
                    content="Calculation Enabled"
                />
            )}
            <ImagePopup
                coordinates={boundingBox}
                mode={IMAGE_POPUP_MODE.CONTROLLED}
                open={!!fieldValue.sourceCellId && !!isActive && imagePopupOpen}
                trigger={
                    getIsDescriptorNumericType(descriptorType) ? (
                        <NumberInput
                            inputRef={inputRef}
                            onFocus={onFocus}
                            className={inputClasses}
                            disabled={
                                descriptorSource === IMPORT_DESCRIPTOR_SOURCE.MASTER_TABLE ||
                                fieldValue.normalizedValueMatchesMaster ||
                                isCalculatedValue
                            }
                            value={fieldValue.normalizedValue as number}
                            onValueChange={(values) => {
                                handleChange(values.floatValue ?? null);
                            }}
                        />
                    ) : (
                        <input
                            ref={inputRef}
                            onFocus={onFocus}
                            disabled={
                                descriptorSource === IMPORT_DESCRIPTOR_SOURCE.MASTER_TABLE ||
                                descriptorType === IMPORT_DESCRIPTOR_TYPE.HEADLINE ||
                                isCalculatedValue
                            }
                            className={inputClasses}
                            type="text"
                            value={fieldValue.normalizedValue ?? ''}
                            onChange={(e) => handleChange(e.target.value)}
                        />
                    )
                }
            />
        </div>
    );
}

type ReadReportCellPropsType = {
    boundingBox: BoundingBoxType;
    fieldValue: FieldValueType;
    descriptorType: ImportDescriptorDataTypeType;
};

function ReadReportCell({ fieldValue, descriptorType, boundingBox }: ReadReportCellPropsType) {
    return (
        <div className="flex full-height flex-align-center">
            {(!!fieldValue.warnings.length || !!fieldValue.confirmations.length) && (
                <InfoPopup
                    correction={fieldValue.correction}
                    confirmations={fieldValue.confirmations}
                    warnings={fieldValue.warnings}
                />
            )}
            <ImagePopup
                mode={fieldValue.sourceCellId ? 'UNCONTROLLED' : 'CONTROLLED'}
                open={!!fieldValue.sourceCellId}
                coordinates={boundingBox}
                trigger={
                    <span className="white-space-no-wrap flex-grow-1 text-align-right">
                        {getIsDescriptorNumericType(descriptorType) && fieldValue.normalizedValue !== null
                            ? numericFormatter(fieldValue.normalizedValue.toString(), {
                                  allowNegative: true,
                                  decimalScale: DECIMAL_SCALE,
                                  decimalSeparator: DECIMAL_SEPARATOR,
                                  thousandSeparator: THOUSAND_SEPARATOR,
                              })
                            : fieldValue.normalizedValue}
                    </span>
                }
            />
        </div>
    );
}
